import { Link } from 'react-router-dom'
import { serviceIllustrations } from '@/components/home/serviceIllustrations'
import { ImageCard } from '@/components/ui/card-21'
import {
  ContainerAnimated,
  ContainerStagger,
  GalleryGrid,
  GalleryGridCell,
} from '@/components/ui/cta-section-with-gallery'
import { getServices } from '@/data/servicesData'
import { useTranslation } from '@/i18n'

/**
 * The homepage's second ask, straight after "who we are": the ask on the left,
 * and on the right a staggered wall of the six services, each card the walker
 * acting that service out and linking to its page. It took the place of the
 * old "What We Do" section, so it carries its `#services` anchor.
 */
export function ClosingCta() {
  const { t, language } = useTranslation()
  const services = getServices(language)

  return (
    <section id="services" className="px-6 sm:px-8 py-16 sm:py-24">
      {/* md:items-start: the text starts level with the first cards instead of
          being centred against the much taller wall. */}
      <div className="mx-auto grid w-full max-w-6xl grid-cols-1 items-center gap-12 md:grid-cols-2 md:items-start">
        {/* No delay before the text: it fades in together with the first
            card rather than after it. Sticky beside the wall: the wall is far
            taller than the text, so the text travels down with the cards
            instead of leaving the lower rows with nothing beside them. */}
        <ContainerStagger
          transition={{ delayChildren: 0, staggerChildren: 0.1 }}
          className="md:sticky md:top-28 md:self-start md:pt-10"
        >
          <ContainerAnimated className="mb-4 block text-sm font-semibold uppercase tracking-wider text-primary-text">
            {t.closingCta.eyebrow}
          </ContainerAnimated>
          <ContainerAnimated>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-foreground text-balance">
              {t.closingCta.heading}
            </h2>
          </ContainerAnimated>
          <ContainerAnimated>
            <p className="my-5 md:my-6 text-base sm:text-lg leading-relaxed text-muted-foreground max-w-xl">
              {t.closingCta.body}
            </p>
          </ContainerAnimated>
          <ContainerAnimated className="flex flex-wrap gap-4">
            <Link
              to="/contact#booking"
              className="inline-block hover-scale min-h-11 bg-primary text-primary-foreground font-semibold px-8 py-3.5 rounded-lg hover:opacity-90 gentle-animation cursor-pointer text-base shadow-lg shadow-primary/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
            >
              {t.closingCta.primary}
            </Link>
            <Link
              to="/contact"
              className="inline-block hover-scale min-h-11 border-2 border-primary text-primary-text font-semibold px-8 py-3.5 rounded-lg hover:bg-primary/5 gentle-animation cursor-pointer text-base focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
            >
              {t.closingCta.secondary}
            </Link>
          </ContainerAnimated>
        </ContainerStagger>

        {/* Taller cells than the gallery's default: each card stacks the
            drawing, the title, a line of description and a button. */}
        <GalleryGrid
          role="list"
          aria-label={t.closingCta.servicesLabel}
          className="grid-rows-[24px_200px_24px_200px_24px_200px_24px] sm:grid-rows-[40px_250px_40px_250px_40px_250px_40px]"
        >
          {services.map((service, index) => {
            const art = serviceIllustrations[service.id]
            if (!art) return null
            return (
              <GalleryGridCell key={service.id} index={index} role="listitem" className="shadow-none overflow-visible">
                <ImageCard
                  to={`/services/${service.id}`}
                  title={service.title}
                  meta={service.cardDescription}
                  ctaLabel={t.closingCta.cardCta}
                  imageUrl={art.light}
                  darkImageUrl={art.dark}
                  themeColor={art.themeColor}
                />
              </GalleryGridCell>
            )
          })}
        </GalleryGrid>
      </div>
    </section>
  )
}
